import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import {
  getOrCreateForecast,
  upsertForecast,
  computeForecastSummary,
  generateItemId,
} from '../services/forecastService';
import { getAllExpenses } from '../services/expenseService';
import { getAllSubscriptions } from '../services/subscriptionService';
import type { MonthlyForecast as MonthlyForecastType, ForecastIncomeItem, ForecastExpenseItem, IncomeCategory, ForecastExpenseCategory, ForecastFrequency } from '../types/forecast';
import type { Expense } from '../types/expense';
import type { Subscription } from '../types/subscription';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Pencil, Trash2,
  TrendingUp, TrendingDown, Wallet, PiggyBank, X, Check, Info,
} from 'lucide-react';
import { APP_CURRENCY } from '../config/currency';
import toast from 'react-hot-toast';

// ─── Constants ───────────────────────────────────────────────────────────────

const INCOME_CATEGORIES: { value: IncomeCategory; label: string }[] = [
  { value: 'salary',     label: 'Salary' },
  { value: 'freelance',  label: 'Freelance' },
  { value: 'business',   label: 'Business' },
  { value: 'investment', label: 'Investment' },
  { value: 'rental',     label: 'Rental' },
  { value: 'bonus',      label: 'Bonus' },
  { value: 'gift',       label: 'Gift' },
  { value: 'other',      label: 'Other' },
];

const EXPENSE_CATEGORIES: { value: ForecastExpenseCategory; label: string; color: string }[] = [
  { value: 'housing',        label: 'Housing',         color: '#818cf8' },
  { value: 'transport',      label: 'Transport',        color: '#34d399' },
  { value: 'food',           label: 'Food & Dining',    color: '#f59e0b' },
  { value: 'utilities',      label: 'Utilities',        color: '#67e8f9' },
  { value: 'subscriptions',  label: 'Subscriptions',    color: '#a78bfa' },
  { value: 'health',         label: 'Health',           color: '#f87171' },
  { value: 'education',      label: 'Education',        color: '#38bdf8' },
  { value: 'shopping',       label: 'Shopping',         color: '#fb923c' },
  { value: 'entertainment',  label: 'Entertainment',    color: '#e879f9' },
  { value: 'savings',        label: 'Savings',          color: '#4ade80' },
  { value: 'debt_repayment', label: 'Debt Repayment',   color: '#fbbf24' },
  { value: 'other',          label: 'Other',            color: '#94a3b8' },
];

const FREQUENCIES: { value: ForecastFrequency; label: string }[] = [
  { value: 'monthly',   label: 'Monthly' },
  { value: 'once',      label: 'One-time' },
  { value: 'weekly',    label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-weekly' },
];

const getCatColor = (cat: string): string =>
  EXPENSE_CATEGORIES.find((c) => c.value === cat)?.color ?? '#94a3b8';

const fmt = (n: number) =>
  `${APP_CURRENCY} ${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('default', {
    month: 'long', year: 'numeric',
  });
};

const currentYM = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonth = (ym: string, delta: number): string => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SummaryCard = ({
  label, planned, actual, icon: Icon, accentClass, isCurrency = true, isBalance = false,
}: {
  label: string;
  planned: number;
  actual?: number;
  icon: React.ElementType;
  accentClass: string;
  isCurrency?: boolean;
  isBalance?: boolean;
}) => {
  const diff = actual !== undefined ? actual - planned : undefined;
  const diffOk = isBalance ? (diff !== undefined ? diff >= 0 : planned >= 0) : (diff !== undefined ? diff <= 0 : true);

  return (
    <div className="glass-card rounded-2xl p-5 fade-in-up flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accentClass}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div>
        <p className="text-xs text-gray-600 mb-0.5">Planned</p>
        <p className="text-xl font-bold text-white">
          {isCurrency ? fmt(planned) : `${planned.toFixed(1)}%`}
        </p>
      </div>
      {actual !== undefined && (
        <div>
          <p className="text-xs text-gray-600 mb-0.5">Actual</p>
          <p className={`text-base font-semibold ${diffOk ? 'text-emerald-400' : 'text-rose-400'}`}>
            {isCurrency ? fmt(actual) : `${actual.toFixed(1)}%`}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Item Form Modal ─────────────────────────────────────────────────────────

type ItemType = 'income' | 'expense';

interface ItemFormProps {
  type: ItemType;
  initial?: ForecastIncomeItem | ForecastExpenseItem | null;
  onSave: (item: ForecastIncomeItem | ForecastExpenseItem) => void;
  onClose: () => void;
}

const ItemForm = ({ type, initial, onSave, onClose }: ItemFormProps) => {
  const isIncome = type === 'income';
  const [name, setName]       = useState(initial?.name ?? '');
  const [category, setCat]    = useState<string>(initial?.category ?? (isIncome ? 'salary' : 'housing'));
  const [amount, setAmount]   = useState(initial ? String(initial.expectedAmount) : '');
  const [freq, setFreq]       = useState<ForecastFrequency>(initial?.frequency ?? 'monthly');
  const [recurring, setRec]   = useState(initial?.isRecurring ?? true);
  const [notes, setNotes]     = useState(initial?.notes ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !amount || isNaN(Number(amount))) {
      toast.error('Please fill in all required fields.');
      return;
    }
    const item = {
      id: initial?.id ?? generateItemId(),
      name: name.trim(),
      category,
      expectedAmount: Number(amount),
      currency: APP_CURRENCY,
      isRecurring: recurring,
      frequency: freq,
      notes: notes.trim() || undefined,
    } as ForecastIncomeItem | ForecastExpenseItem;
    onSave(item);
  };

  const cats = isIncome ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop p-4">
      <div className="glass-card rounded-2xl p-6 w-full max-w-md border border-white/10 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white">
            {initial ? 'Edit' : 'Add'} {isIncome ? 'Income' : 'Expense'} Item
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/5">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Name *</label>
            <input
              id="forecast-item-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isIncome ? 'e.g. Monthly Salary' : 'e.g. Rent'}
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500 transition-colors"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Category *</label>
            <select
              id="forecast-item-category"
              value={category}
              onChange={(e) => setCat(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors appearance-none"
            >
              {cats.map((c) => (
                <option key={c.value} value={c.value} className="bg-[#1a1a2e]">{c.label}</option>
              ))}
            </select>
          </div>

          {/* Amount + Frequency */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Amount ({APP_CURRENCY}) *</label>
              <input
                id="forecast-item-amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500 transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Frequency</label>
              <select
                id="forecast-item-frequency"
                value={freq}
                onChange={(e) => setFreq(e.target.value as ForecastFrequency)}
                className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors appearance-none"
              >
                {FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value} className="bg-[#1a1a2e]">{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Recurring toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div
              id="forecast-item-recurring"
              onClick={() => setRec((r) => !r)}
              className={`w-10 h-5 rounded-full transition-colors relative ${recurring ? 'bg-indigo-600' : 'bg-white/10'}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${recurring ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-gray-300">Recurring</span>
          </label>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Notes (optional)</label>
            <input
              id="forecast-item-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details…"
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-white/10 text-sm text-gray-400 hover:text-white hover:border-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              id="forecast-item-save"
              className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Check className="h-4 w-4" /> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Items Table ─────────────────────────────────────────────────────────────

function ItemsTable<T extends ForecastIncomeItem | ForecastExpenseItem>({
  items, type, onAdd, onEdit, onDelete,
}: {
  items: T[];
  type: ItemType;
  onAdd: () => void;
  onEdit: (item: T) => void;
  onDelete: (id: string) => void;
}) {
  const isIncome = type === 'income';
  const total = items.reduce((s, i) => s + i.expectedAmount, 0);

  return (
    <div className="glass-card rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {isIncome
            ? <TrendingUp className="h-4 w-4 text-emerald-400" />
            : <TrendingDown className="h-4 w-4 text-rose-400" />}
          <h2 className="text-sm font-semibold text-white">
            {isIncome ? 'Income Sources' : 'Planned Expenses'}
          </h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-400">
            {items.length}
          </span>
        </div>
        <button
          id={`forecast-add-${type}`}
          onClick={onAdd}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>

      {items.length === 0 ? (
        <div className="py-10 text-center text-gray-600 text-sm">
          No {isIncome ? 'income sources' : 'expense items'} yet — click Add to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const catLabel = isIncome
              ? INCOME_CATEGORIES.find((c) => c.value === item.category)?.label ?? item.category
              : EXPENSE_CATEGORIES.find((c) => c.value === item.category)?.label ?? item.category;
            return (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 rounded-xl bg-white/3 border border-white/5 hover:bg-white/5 transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {!isIncome && (
                    <div
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: getCatColor(item.category) }}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{item.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {catLabel} · {FREQUENCIES.find((f) => f.value === item.frequency)?.label ?? item.frequency}
                      {item.isRecurring && <span className="ml-1.5 text-indigo-400">↺</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                  <span className={`text-sm font-semibold ${isIncome ? 'text-emerald-400' : 'text-white'}`}>
                    {fmt(item.expectedAmount)}
                  </span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => onEdit(item as T)}
                      className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/8 transition-colors"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(item.id)}
                      className="p-1.5 rounded-lg text-gray-500 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {items.length > 0 && (
        <div className={`mt-4 pt-4 border-t border-white/5 flex justify-between text-sm font-semibold ${isIncome ? 'text-emerald-400' : 'text-white'}`}>
          <span>Total Planned {isIncome ? 'Income' : 'Expenses'}</span>
          <span>{fmt(total)}</span>
        </div>
      )}
    </div>
  );
}

// ─── Bar Chart ───────────────────────────────────────────────────────────────

const ForecastBarChart = ({
  planned,
  actual,
}: {
  planned: Record<string, number>;
  actual: Record<string, number>;
}) => {
  const allKeys = Array.from(new Set([...Object.keys(planned), ...Object.keys(actual)]));
  if (allKeys.length === 0) return null;

  const max = Math.max(
    ...allKeys.map((k) => Math.max(planned[k] ?? 0, actual[k] ?? 0)),
    1
  );

  return (
    <div className="glass-card rounded-2xl p-5 fade-in-up">
      <div className="flex items-center gap-2 mb-5">
        <h2 className="text-sm font-semibold text-white">Planned vs. Actual Expenses</h2>
        <div className="flex items-center gap-3 ml-auto text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-500/60 inline-block" /> Planned</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400/70 inline-block" /> Actual</span>
        </div>
      </div>

      <div className="space-y-4">
        {allKeys.map((key) => {
          const p = planned[key] ?? 0;
          const a = actual[key] ?? 0;
          const catLabel = EXPENSE_CATEGORIES.find((c) => c.value === key)?.label ?? key;
          const color = getCatColor(key);
          const overBudget = a > p && p > 0;

          return (
            <div key={key}>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-gray-400 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
                  {catLabel}
                </span>
                <span className={`font-medium ${overBudget ? 'text-rose-400' : 'text-gray-300'}`}>
                  {fmt(a)} / {fmt(p)}
                  {overBudget && <span className="ml-1 text-rose-400">↑</span>}
                </span>
              </div>
              {/* Planned bar */}
              <div className="h-2 bg-white/5 rounded-full overflow-hidden mb-1">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${(p / max) * 100}%`, background: `${color}55` }}
                />
              </div>
              {/* Actual bar */}
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${Math.min((a / max) * 100, 100)}%`,
                    background: overBudget ? '#f87171' : color,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const MonthlyForecast = () => {
  const [month, setMonth] = useState(currentYM());
  const [forecast, setForecast] = useState<MonthlyForecastType | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');

  // Item modal state
  const [itemModal, setItemModal] = useState<{
    open: boolean;
    type: ItemType;
    editing: ForecastIncomeItem | ForecastExpenseItem | null;
  }>({ open: false, type: 'income', editing: null });

  const load = useCallback(async () => {
    setLoading(true);
    const [fc, exp, subs] = await Promise.all([
      getOrCreateForecast(month),
      getAllExpenses(),
      getAllSubscriptions(),
    ]);
    setForecast(fc);
    setNotes(fc.notes ?? '');
    setExpenses(exp);
    setSubscriptions(subs);
    setLoading(false);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  // ── Save helpers ───────────────────────────────────────────────────────────

  const save = async (updated: MonthlyForecastType) => {
    setSaving(true);
    const saved = await upsertForecast(updated);
    if (saved) {
      setForecast(saved);
      setNotes(saved.notes ?? '');
      toast.success('Forecast saved');
    } else {
      toast.error('Failed to save forecast');
    }
    setSaving(false);
  };

  const handleSaveNotes = async () => {
    if (!forecast) return;
    await save({ ...forecast, notes });
  };

  // ── Income item CRUD ───────────────────────────────────────────────────────

  const handleAddIncome = () => setItemModal({ open: true, type: 'income', editing: null });
  const handleEditIncome = (item: ForecastIncomeItem) =>
    setItemModal({ open: true, type: 'income', editing: item });

  const handleDeleteIncome = async (id: string) => {
    if (!forecast) return;
    const updated = {
      ...forecast,
      incomeItems: forecast.incomeItems.filter((i) => i.id !== id),
    };
    await save(updated);
  };

  // ── Expense item CRUD ──────────────────────────────────────────────────────

  const handleAddExpense = () => setItemModal({ open: true, type: 'expense', editing: null });
  const handleEditExpense = (item: ForecastExpenseItem) =>
    setItemModal({ open: true, type: 'expense', editing: item });

  const handleDeleteExpense = async (id: string) => {
    if (!forecast) return;
    const updated = {
      ...forecast,
      expenseItems: forecast.expenseItems.filter((i) => i.id !== id),
    };
    await save(updated);
  };

  // ── Modal save ─────────────────────────────────────────────────────────────

  const handleItemSave = async (item: ForecastIncomeItem | ForecastExpenseItem) => {
    if (!forecast) return;
    if (itemModal.type === 'income') {
      const items = forecast.incomeItems;
      const idx = items.findIndex((i) => i.id === item.id);
      const updated = idx >= 0
        ? items.map((i) => (i.id === item.id ? item as ForecastIncomeItem : i))
        : [...items, item as ForecastIncomeItem];
      await save({ ...forecast, incomeItems: updated });
    } else {
      const items = forecast.expenseItems;
      const idx = items.findIndex((i) => i.id === item.id);
      const updated = idx >= 0
        ? items.map((i) => (i.id === item.id ? item as ForecastExpenseItem : i))
        : [...items, item as ForecastExpenseItem];
      await save({ ...forecast, expenseItems: updated });
    }
    setItemModal({ open: false, type: 'income', editing: null });
  };

  // ── Summary ────────────────────────────────────────────────────────────────

  const summary = forecast
    ? computeForecastSummary(forecast, expenses, subscriptions)
    : null;

  return (
    <div className="min-h-screen flex" style={{ background: '#0f0f13' }}>
      <Sidebar />

      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-6 max-w-7xl mx-auto">

          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 fade-in-up">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-violet-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Monthly Forecast</h1>
                <p className="text-sm text-gray-500 mt-0.5">Plan and track your income & expenses</p>
              </div>
            </div>

            {/* Month navigator */}
            <div className="flex items-center gap-2 bg-white/5 border border-white/8 rounded-2xl px-2 py-1.5">
              <button
                id="forecast-prev-month"
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
                className="p-1.5 rounded-xl text-gray-400 hover:text-white hover:bg-white/8 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-semibold text-white px-2 min-w-[140px] text-center">
                {monthLabel(month)}
              </span>
              <button
                id="forecast-next-month"
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
                className="p-1.5 rounded-xl text-gray-400 hover:text-white hover:bg-white/8 transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="animate-spin h-8 w-8 border-2 border-indigo-400 border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {/* ── Summary Cards ─────────────────────────────────────────── */}
              {summary && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <SummaryCard
                    label="Expected Income"
                    planned={summary.expectedIncome}
                    actual={summary.actualIncome > 0 ? summary.actualIncome : undefined}
                    icon={TrendingUp}
                    accentClass="bg-emerald-500/15 text-emerald-400"
                  />
                  <SummaryCard
                    label="Planned Expenses"
                    planned={summary.expectedExpenses}
                    actual={summary.actualExpenses}
                    icon={TrendingDown}
                    accentClass="bg-rose-500/15 text-rose-400"
                  />
                  <SummaryCard
                    label="Projected Balance"
                    planned={summary.projectedBalance}
                    actual={summary.actualBalance}
                    icon={Wallet}
                    accentClass="bg-indigo-500/15 text-indigo-400"
                    isBalance
                  />
                  <SummaryCard
                    label="Savings Rate"
                    planned={summary.savingsRate}
                    actual={summary.actualSavingsRate}
                    icon={PiggyBank}
                    accentClass="bg-violet-500/15 text-violet-400"
                    isCurrency={false}
                  />
                </div>
              )}

              {/* ── Actual vs. Planned info banner ─────────────────────────── */}
              {summary && summary.actualExpenses > 0 && (
                <div className="flex items-start gap-3 p-4 mb-6 rounded-xl bg-indigo-500/8 border border-indigo-500/20 fade-in-up">
                  <Info className="h-4 w-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Actual expenses include all recorded spending for <strong className="text-white">{monthLabel(month)}</strong> plus
                    the equivalent monthly cost of your active subscriptions.
                    {summary.actualExpenses > summary.expectedExpenses && (
                      <span className="text-rose-400 ml-1">
                        You are <strong>{fmt(summary.actualExpenses - summary.expectedExpenses)}</strong> over your planned budget.
                      </span>
                    )}
                    {summary.actualExpenses <= summary.expectedExpenses && summary.expectedExpenses > 0 && (
                      <span className="text-emerald-400 ml-1">
                        You are <strong>{fmt(summary.expectedExpenses - summary.actualExpenses)}</strong> under budget — great job!
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* ── Income & Expense Tables ───────────────────────────────── */}
              <div className="grid lg:grid-cols-2 gap-6 mb-8">
                <ItemsTable<ForecastIncomeItem>
                  items={forecast?.incomeItems ?? []}
                  type="income"
                  onAdd={handleAddIncome}
                  onEdit={handleEditIncome}
                  onDelete={handleDeleteIncome}
                />
                <ItemsTable<ForecastExpenseItem>
                  items={forecast?.expenseItems ?? []}
                  type="expense"
                  onAdd={handleAddExpense}
                  onEdit={handleEditExpense}
                  onDelete={handleDeleteExpense}
                />
              </div>

              {/* ── Bar Chart ─────────────────────────────────────────────── */}
              {summary && (
                Object.keys(summary.categoryPlanned).length > 0 ||
                Object.keys(summary.categoryActuals).length > 0
              ) && (
                <div className="mb-8">
                  <ForecastBarChart
                    planned={summary?.categoryPlanned ?? {}}
                    actual={summary?.categoryActuals ?? {}}
                  />
                </div>
              )}

              {/* ── Month Notes ───────────────────────────────────────────── */}
              <div className="glass-card rounded-2xl p-5 fade-in-up">
                <h2 className="text-sm font-semibold text-white mb-3">Month Notes</h2>
                <textarea
                  id="forecast-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={`Add notes for ${monthLabel(month)}…`}
                  className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
                />
                <div className="flex justify-end mt-3">
                  <button
                    id="forecast-save-notes"
                    onClick={handleSaveNotes}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium transition-colors"
                  >
                    {saving
                      ? <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                      : <Check className="h-3.5 w-3.5" />}
                    Save Notes
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Item form modal */}
      {itemModal.open && (
        <ItemForm
          type={itemModal.type}
          initial={itemModal.editing}
          onSave={handleItemSave}
          onClose={() => setItemModal({ open: false, type: 'income', editing: null })}
        />
      )}
    </div>
  );
};

export default MonthlyForecast;
